// src/CourseContent/TopicView.jsx

import React, { useEffect, useState, useContext, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../component/Api/instance";
import Dialog from "../component/ui/Dialog";
import { PrimaryButton, SecondaryButton } from "../component/ui/Buttons";
import LockIcon from "@mui/icons-material/Lock";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GetAppIcon from "@mui/icons-material/GetApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import SpeedIcon from "@mui/icons-material/Speed";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { styled } from "@mui/material/styles";

// Styled Components for Netflix-style LMS layout
const LMSContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
}));

const SidebarPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  minHeight: "85vh",
  padding: "24px 0",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
}));

const SidebarHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.125rem",
  padding: "0 24px",
  marginBottom: "16px",
  color: theme.palette.primary.main,
}));

const SidebarItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 24px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  borderLeft: "4px solid transparent",
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active ? `${theme.palette.primary.main}0D` : "transparent",
  borderLeftColor: active ? theme.palette.primary.main : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "0.875rem",
  "&:hover": {
    backgroundColor: active ? `${theme.palette.primary.main}1A` : theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const IndexPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "24px",
  height: "24px",
  borderRadius: "6px",
  fontSize: "0.75rem",
  fontWeight: 700,
  backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
  color: active ? "#FFFFFF" : theme.palette.text.secondary,
}));

const MainArea = styled(Box)(({ theme }) => ({
  padding: "32px 40px",
  [theme.breakpoints.down("md")]: {
    padding: "24px 16px",
  },
}));

const PlayerWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: "20px",
  overflow: "hidden",
  backgroundColor: "#000000",
  aspectRatio: "16/9",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
  marginBottom: "28px",
}));

const ResourceCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "20px",
  padding: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "24px",
  boxShadow: "none",
  gap: "16px",
  flexWrap: "wrap",
}));

export default function TopicView() {
  const { sno } = useParams();
  const navigate = useNavigate();
  const email = localStorage.getItem("userMail");

  const [topics, setTopics] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const speedOptions = [
    { label: "0.5x", value: 0.5 },
    { label: "0.75x", value: 0.75 },
    { label: "1x (Normal)", value: 1.0 },
    { label: "1.25x", value: 1.25 },
    { label: "1.5x", value: 1.5 },
    { label: "2x", value: 2.0 },
  ];

  const handleSpeedChange = (speed) => {
    const numSpeed = Number(speed);
    setPlaybackSpeed(numSpeed);
    if (videoRef.current) {
      videoRef.current.playbackRate = numSpeed;
    }
  };

  const applyPlaybackSpeed = () => {
    if (videoRef.current) {
      videoRef.current.playbackRate = Number(playbackSpeed);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error("Error attempting to exit fullscreen:", err);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // const FILE_BASE = "https://nursingupdate.in/Nursing/controllers/api/admin/";
  // const FILE_BASE = "http://localhost/vb_nursing_be/controllers/api/admin/";
  const FILE_BASE = "https://nursingupdate.in/testing_be/controllers/api/admin/";

  const fixFilePath = (path) => {
    if (!path) return "";
    return FILE_BASE + path.replace("../", "");
  };

  /* ================= SECURITY BLOCK ================= */

  useEffect(() => {
    const blockKeys = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && e.key === "I") ||
        (e.ctrlKey && e.key === "U")
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", blockKeys);
    document.addEventListener("contextmenu", (e) => e.preventDefault());

    return () => {
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  /* ===== Video lock when tab change ===== */

  useEffect(() => {
    const getVideo = () => document.querySelector("video");

    const lock = () => {
      const video = getVideo();
      if (!video) return;
      video.pause();
      video.muted = true;
    };

    const unlock = () => {
      const video = getVideo();
      if (!video) return;
      video.muted = false;
    };

    window.addEventListener("blur", lock);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) lock();
    });
    window.addEventListener("focus", unlock);

    return () => {
      window.removeEventListener("blur", lock);
      window.removeEventListener("focus", unlock);
    };
  }, []);

  /* ================= FETCH TOPICS ================= */

  useEffect(() => {
    fetchTopicList();
  }, [sno]);

  const fetchTopicList = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("get/U_ViewCourseTopicList.php", {
        userId: email,
        subId: sno,
      });

      if (res.data?.message === "timeout") {
        navigate("/signin");
        return;
      }

      const list = Array.isArray(res.data) ? res.data : [];
      setTopics(list);

      if (list.length > 0) {
        handleTopicClick(list[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= CLICK TOPIC ================= */

  const handleTopicClick = async (topicId) => {
    try {
      const res = await axiosInstance.post("get/U_ViewCourseTopicDetails.php", {
        userId: email,
        topicId,
      });

      if (!Array.isArray(res.data) || res.data.length === 0) return;

      const topic = res.data[0];

      if (Number(topic.ispremium) === 1) {
        setOpenPopup(true);
        return;
      }

      setActiveTopicId(topicId);
      setTopicDetails(topic);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= NEXT TOPIC ================= */

  const goNextTopic = () => {
    const index = topics.findIndex((t) => t.id === activeTopicId);

    if (topics[index + 1]) {
      handleTopicClick(topics[index + 1].id);
    }
  };

  const goPrevTopic = () => {
    const index = topics.findIndex((t) => t.id === activeTopicId);

    if (topics[index - 1]) {
      handleTopicClick(topics[index - 1].id);
    }
  };

  const hasNext = topics.findIndex((t) => t.id === activeTopicId) < topics.length - 1;
  const hasPrev = topics.findIndex((t) => t.id === activeTopicId) > 0;

  return (
    <LMSContainer>
      <Container fluid style={{ padding: 0 }}>
        <Row className="g-0">
          
          {/* LEFT SIDEBAR PANEL */}
          <Col xs={12} md={4} lg={3}>
            <SidebarPanel>
              <SidebarHeader>Course Topics</SidebarHeader>
              
              {loading ? (
                <Box style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : topics.length === 0 ? (
                <Typography variant="body1" color="textSecondary" style={{ padding: "0 24px" }}>
                  No topics uploaded.
                </Typography>
              ) : (
                <Box style={{ overflowY: "auto", maxH: "75vh" }}>
                  {topics.map((t, i) => (
                    <SidebarItem
                      key={t.id}
                      active={t.id === activeTopicId}
                      onClick={() => handleTopicClick(t.id)}
                    >
                      <IndexPill active={t.id === activeTopicId}>
                        {i + 1}
                      </IndexPill>
                      <span style={{ flex: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {t.topic}
                      </span>
                      {Number(t.ispremium) === 1 && (
                        <LockIcon fontSize="inherit" color="action" />
                      )}
                    </SidebarItem>
                  ))}
                </Box>
              )}
            </SidebarPanel>
          </Col>

          {/* MAIN CONTENT AREA */}
          <Col xs={12} md={8} lg={9}>
            {topicDetails ? (
              <MainArea>
                
                {/* Topic Title */}
                <Typography variant="h2" style={{ fontWeight: 800, marginBottom: "8px" }}>
                  {topicDetails.topic}
                </Typography>
                
                {/* Description block */}
                <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6, marginBottom: "28px" }}>
                  {topicDetails.topic_desc}
                </Typography>

                {/* VIDEO PLAYER */}
                {topicDetails.topic_video && (
                  <PlayerWrapper
                    ref={containerRef}
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: isFullscreen ? "100vh" : "auto",
                      aspectRatio: isFullscreen ? "unset" : "16/9",
                      borderRadius: isFullscreen ? 0 : "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#000000",
                      "&:hover .user-corner-speed-btn": {
                        opacity: 1,
                      },
                    }}
                  >
                    <video
                      ref={videoRef}
                      key={topicDetails.topic_video}
                      controls
                      controlsList="nodownload noplaybackrate"
                      disablePictureInPicture
                      disableRemotePlayback
                      onLoadedMetadata={applyPlaybackSpeed}
                      onPlay={applyPlaybackSpeed}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                    >
                      <source
                        src={fixFilePath(topicDetails.topic_video)}
                        type="video/mp4"
                      />
                    </video>

                    {/* Floating Corner Buttons (Speed & Fullscreen) */}
                    <Box
                      className="user-corner-speed-btn"
                      sx={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        onClick={(e) => setSpeedMenuAnchor(e.currentTarget)}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.65)",
                          color: "#ffffff",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.25)",
                          borderRadius: "20px",
                          px: 1.2,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.85)",
                          },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#ffffff" }}>
                          {playbackSpeed}x
                        </Typography>
                      </IconButton>

                      <IconButton
                        onClick={toggleFullscreen}
                        size="small"
                        title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.65)",
                          color: "#ffffff",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.25)",
                          borderRadius: "50%",
                          p: 0.6,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.85)",
                          },
                        }}
                      >
                        {isFullscreen ? (
                          <FullscreenExitIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <FullscreenIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Box>

                    {/* Material UI Speed Menu */}
                    <Menu
                      container={containerRef.current || undefined}
                      anchorEl={speedMenuAnchor}
                      open={Boolean(speedMenuAnchor)}
                      onClose={() => setSpeedMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      PaperProps={{
                        sx: {
                          borderRadius: "14px",
                          minWidth: 170,
                          boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                          border: "1px solid rgba(255,255,255,0.15)",
                          backgroundColor: isFullscreen ? "rgba(15, 23, 42, 0.95)" : "#ffffff",
                          color: isFullscreen ? "#f8fafc" : "#1e293b",
                          backdropFilter: "blur(10px)",
                          p: 0.5,
                        },
                      }}
                    >
                      <Box sx={{ px: 2, py: 1, borderBottom: isFullscreen ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f1f5f9" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isFullscreen ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Playback Speed
                        </Typography>
                      </Box>
                      {speedOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          selected={playbackSpeed === opt.value}
                          onClick={() => {
                            handleSpeedChange(opt.value);
                            setSpeedMenuAnchor(null);
                          }}
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: playbackSpeed === opt.value ? 700 : 500,
                            color: playbackSpeed === opt.value ? "#2563eb" : (isFullscreen ? "#e2e8f0" : "#334155"),
                            display: "flex",
                            justifyContent: "space-between",
                            borderRadius: "8px",
                            my: 0.3,
                            py: 0.8,
                            "&:hover": {
                              backgroundColor: isFullscreen ? "rgba(255,255,255,0.08)" : "#f8fafc",
                            },
                          }}
                        >
                          {opt.label}
                          {playbackSpeed === opt.value && (
                            <CheckIcon sx={{ fontSize: 16, color: "#2563eb", ml: 1 }} />
                          )}
                        </MenuItem>
                      ))}
                    </Menu>
                  </PlayerWrapper>
                )}

                {/* PDF & NAVIGATION WIDGET */}
                <Row className="g-4">
                  {topicDetails.topic_pdf && (
                    <Col xs={12} md={6}>
                      <ResourceCard>
                        <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(220, 38, 38, 0.1)", color: "var(--color-danger)" }}>
                            <DescriptionIcon />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                              Topic Lecture Notes
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Portable PDF document
                            </Typography>
                          </Box>
                        </Box>
                        <a
                          href={fixFilePath(topicDetails.topic_pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          style={{ textDecoration: "none" }}
                        >
                          <PrimaryButton startIcon={<GetAppIcon />} style={{ height: "38px", borderRadius: "12px" }}>
                            Download Notes
                          </PrimaryButton>
                        </a>
                      </ResourceCard>
                    </Col>
                  )}

                  <Col xs={12} md={topicDetails.topic_pdf ? 6 : 12}>
                    <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px", height: "100%", paddingTop: topicDetails.topic_pdf ? "24px" : "0" }}>
                      {hasPrev && (
                        <SecondaryButton 
                          onClick={goPrevTopic}
                          startIcon={<ArrowBackIcon />}
                          style={{ height: "42px", minWidth: "120px", borderRadius: "14px" }}
                        >
                          Prev Lecture
                        </SecondaryButton>
                      )}
                      {hasNext && (
                        <PrimaryButton 
                          onClick={goNextTopic}
                          endIcon={<ArrowForwardIcon />}
                          style={{ height: "42px", minWidth: "120px", borderRadius: "14px" }}
                        >
                          Next Lecture
                        </PrimaryButton>
                      )}
                    </Box>
                  </Col>
                </Row>

              </MainArea>
            ) : (
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "24px" }}>
                <PlayCircleOutlineIcon style={{ fontSize: 64, color: "var(--color-border)", marginBottom: "16px" }} />
                <Typography variant="body1" color="textSecondary">
                  Select a topic from the sidebar to begin watching lessons.
                </Typography>
              </Box>
            )}
          </Col>
        </Row>
      </Container>

      {/* PREMIUM UPGRADE POPUP */}
      <Dialog 
        open={openPopup} 
        onClose={() => setOpenPopup(false)} 
        title="Premium Content 🔒"
      >
        <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6, marginBottom: "20px" }}>
          This lecture is available for Premium users only. Upgrade your membership plan to unlock all syllabus categories, clinical lecture videos, and subject mock examinations.
        </Typography>
        <Box style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
          <SecondaryButton onClick={() => setOpenPopup(false)}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={() => { setOpenPopup(false); navigate("/premiumplan"); }}>
            Upgrade Now
          </PrimaryButton>
        </Box>
      </Dialog>
    </LMSContainer>
  );
}